import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { UserPlus, Loader2 } from 'lucide-react';
import apiService from '../../../services/api';

const AddAdminDialog = ({ open, onOpenChange, shopId, shopName, onSuccess }) => {
  const [adminData, setAdminData] = useState({
    fullname: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [adding, setAdding] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!adminData.fullname.trim()) newErrors.fullname = 'Name is required';
    if (!adminData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!adminData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone is required';
    if (!adminData.password) {
      newErrors.password = 'Password is required';
    } else if (adminData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (adminData.password !== adminData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = async () => {
    if (!validate()) return;
    
    setAdding(true);
    try {
      const response = await apiService.createShopUser({
        shopMongoId: shopId,
        fullname: adminData.fullname,
        email: adminData.email.toLowerCase(),
        phoneNumber: adminData.phoneNumber,
        role: 'shopAdmin',
        password: adminData.password,
        confirmPassword: adminData.confirmPassword,
      });
      
      if (response.success) {
        onSuccess();
        handleClose();
      } else {
        setErrors({ general: response.message || 'Failed to add admin' });
      }
    } catch (err) {
      setErrors({ general: err.message || 'Failed to add admin' });
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setAdminData({
      fullname: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
    });
    setErrors({});
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0]">
        <DialogHeader>
          <DialogTitle className="text-[#E2E8F0]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Add Admin User
          </DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            Add a new admin user to {shopName}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {errors.general && (
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md p-3">
              <p className="text-[#EF4444] text-sm">{errors.general}</p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="adminName" className="text-[#E2E8F0]">Full Name</Label>
            <Input
              id="adminName"
              value={adminData.fullname}
              onChange={(e) => setAdminData(prev => ({ ...prev, fullname: e.target.value }))}
              placeholder="Enter full name"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${errors.fullname ? 'border-[#EF4444]' : ''}`}
            />
            {errors.fullname && <p className="text-[#EF4444] text-xs">{errors.fullname}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="adminEmail" className="text-[#E2E8F0]">Email</Label>
            <Input
              id="adminEmail"
              type="email"
              value={adminData.email}
              onChange={(e) => setAdminData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="admin@example.com"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${errors.email ? 'border-[#EF4444]' : ''}`}
            />
            {errors.email && <p className="text-[#EF4444] text-xs">{errors.email}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="adminPhone" className="text-[#E2E8F0]">Phone</Label>
            <Input
              id="adminPhone"
              value={adminData.phoneNumber}
              onChange={(e) => setAdminData(prev => ({ ...prev, phoneNumber: e.target.value }))}
              placeholder="310-555-0102"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${errors.phoneNumber ? 'border-[#EF4444]' : ''}`}
            />
            {errors.phoneNumber && <p className="text-[#EF4444] text-xs">{errors.phoneNumber}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="adminPassword" className="text-[#E2E8F0]">Password</Label>
            <Input
              id="adminPassword"
              type="password"
              value={adminData.password}
              onChange={(e) => setAdminData(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Minimum 8 characters"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${errors.password ? 'border-[#EF4444]' : ''}`}
            />
            {errors.password && <p className="text-[#EF4444] text-xs">{errors.password}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="adminConfirmPassword" className="text-[#E2E8F0]">Confirm Password</Label>
            <Input
              id="adminConfirmPassword"
              type="password"
              value={adminData.confirmPassword}
              onChange={(e) => setAdminData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              placeholder="Re-enter password"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${errors.confirmPassword ? 'border-[#EF4444]' : ''}`}
            />
            {errors.confirmPassword && <p className="text-[#EF4444] text-xs">{errors.confirmPassword}</p>}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={adding}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Add Admin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddAdminDialog;
